// src/components/SuggestGameModal.tsx
// 🆕 UPDATED: Allow multiple game selection
import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonSpinner,
  IonText,
  IonSearchbar,
  IonNote,
  IonCheckbox,
  IonBadge,
} from '@ionic/react';
import { close, checkmark } from 'ionicons/icons';
import { profileService } from '../services/profileService';
import { sessionService } from '../services/sessionService';
import { UserGame } from '../types/profile';

interface SuggestGameModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  sessionId: string;
  alreadySuggestedGameIds: string[]; // Already suggested game IDs
  onSuggestComplete?: () => void;
}

export const SuggestGameModal: React.FC<SuggestGameModalProps> = ({
  isOpen,
  onDismiss,
  sessionId,
  alreadySuggestedGameIds,
  onSuggestComplete,
}) => {
  const [userGames, setUserGames] = useState<UserGame[]>([]);
  const [selectedGameIds, setSelectedGameIds] = useState<string[]>([]); // 🆕 Changed to array
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadGames();
      setSelectedGameIds([]); // 🆕 Reset to empty array
      setSearchText('');
    }
  }, [isOpen]);

  const loadGames = async () => {
    setLoading(true);
    const games = await profileService.getUserGames();

    // Filter out already suggested games
    const available = games.filter(g => !alreadySuggestedGameIds.includes(g.game_id));
    setUserGames(available);
    setLoading(false);
  };

  // 🆕 Toggle game selection
  const toggleGameSelection = (gameId: string) => {
    setSelectedGameIds(prev => {
      if (prev.includes(gameId)) {
        return prev.filter(id => id !== gameId);
      } else {
        return [...prev, gameId];
      }
    });
  };

  // 🆕 Suggest multiple games
  const handleSuggest = async () => {
    if (selectedGameIds.length === 0) return;

    setSuggesting(true);

    // Suggest all selected games in parallel
    const results = await Promise.all(
      selectedGameIds.map(gameId =>
        sessionService.suggestGame(sessionId, gameId)
      )
    );

    // Check if all succeeded
    const allSucceeded = results.every(r => r.success);

    if (allSucceeded) {
      if (onSuggestComplete) onSuggestComplete();
      onDismiss();
    } else {
      const failedCount = results.filter(r => !r.success).length;
      alert(`Failed to suggest ${failedCount} game(s). Please try again.`);
    }

    setSuggesting(false);
  };

  const filteredGames = userGames.filter(game =>
    game.game?.name.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={onDismiss} disabled={suggesting}>
              <IonIcon icon={close} />
            </IonButton>
          </IonButtons>
          <IonTitle>
            Suggest Games
            {selectedGameIds.length > 0 && (
              <IonBadge color="primary" style={{ marginLeft: '8px' }}>
                {selectedGameIds.length}
              </IonBadge>
            )}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={handleSuggest}
              disabled={selectedGameIds.length === 0 || suggesting}
              strong
            >
              {suggesting ? <IonSpinner name="crescent" /> : <IonIcon icon={checkmark} />}
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={searchText}
            onIonInput={(e) => setSearchText(e.detail.value || '')}
            placeholder="Search your games"
            disabled={suggesting}
          />
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <IonSpinner name="crescent" />
          </div>
        ) : filteredGames.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <IonText color="medium">
              <p>
                {userGames.length === 0
                  ? 'No games in your collection to suggest'
                  : 'No games match your search'}
              </p>
              {userGames.length === 0 && (
                <IonNote>
                  <p>Add games to your profile first to suggest them for sessions!</p>
                </IonNote>
              )}
            </IonText>
          </div>
        ) : (
          <>
            <div style={{ padding: '16px', background: 'var(--ion-color-light)' }}>
              <IonText color="medium">
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {selectedGameIds.length === 0
                    ? 'Select one or more games to suggest'
                    : `${selectedGameIds.length} game${selectedGameIds.length > 1 ? 's' : ''} selected`}
                </p>
              </IonText>
            </div>
            <IonList>
              {filteredGames.map((userGame) => {
                const isSelected = selectedGameIds.includes(userGame.game_id);
                return (
                  <IonItem
                    key={userGame.id}
                    button
                    onClick={() => !suggesting && toggleGameSelection(userGame.game_id)}
                    disabled={suggesting}
                  >
                    <IonCheckbox
                      slot="start"
                      checked={isSelected}
                      disabled={suggesting}
                    />
                    <IonLabel>
                      <h2>{userGame.game?.name || 'Unknown Game'}</h2>
                      {userGame.skill_level && (
                        <p>Your skill: {userGame.skill_level}</p>
                      )}
                    </IonLabel>
                    {isSelected && (
                      <IonIcon icon={checkmark} slot="end" color="primary" />
                    )}
                  </IonItem>
                );
              })}
            </IonList>
          </>
        )}
      </IonContent>
    </IonModal>
  );
};